import { OptionsApi } from '@core/swagger-client';
import { ClientType, ExamResultType, QuestionType } from '@core/types';
import { Button, CompositePageHeader, ContainerNoTopSpace, ErrorMessagePage, Heading, LoaderPage, PageHeader, PageHeaderContent, TabGroup } from '@core/ui';
import { useLocation, useNavigate, useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PollCard } from '../../molecules';
import { ExamResultsDetails } from '../../organisms';

const emptyGuid = '00000000-0000-0000-0000-000000000000';

export const ExamResults = () => {
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();

    const tabs = ['overview', 'details'];
    const { hash } = useLocation();
    let activeTab = tabs.findIndex((t) => t === hash);
    if (activeTab === -1) {
        activeTab = 0;
    }
    const navigate = useNavigate();

    const changeTab = (no: number) => {
        navigate({ hash: tabs[no] });
    };

    const { data: webinar, isLoading: isWebinarLoading, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const { data: answers, isLoading: isAnswersLoading, error: answersError } = useQuery(OptionsApi.insights.getExamAnswers(webinarId!));
    const { data: results, isLoading: isResultsLoading, error: resultsError } = useQuery(OptionsApi.insights.getExamResults(webinarId!));
    const { data: exam, isLoading: isExamLoading, error: examError } = useQuery(OptionsApi.exam.get(webinarId!));

    const error = webinarError ?? answersError ?? examError ?? resultsError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isWebinarLoading || isExamLoading || isAnswersLoading || isResultsLoading || !webinar || !exam || !answers || !results) {
        return <LoaderPage />;
    }

    const getAnsweredCount = (questionId: string) => {
        let count = 0;
        answers.forEach((participant) => {
            const answer = participant.answers.find((a) => a.questionId === questionId);
            if (answer && (answer.selectedAnswers?.length || answer.openAnswer)) {
                count++;
            }
        });
        return count;
    };

    const getAnswerCount = (questionId: string, answerId?: string) => {
        let count = 0;
        answers.forEach((participant) => {
            const answer = participant.answers.find((a) => a.questionId === questionId);
            if (!answer) {
                return;
            }

            if (answerId === undefined || answerId === emptyGuid) {
                if (answer.openAnswer) {
                    count++;
                }
            } else {
                if (answer.selectedAnswers && answer.selectedAnswers.includes(answerId)) {
                    count++;
                }
            }
        });

        return count;
    };

    // We cheat in this page by using Poll components to display results
    const getExamAnswers = (questionId: string) => {
        const question = exam.questions.find((q) => q.questionId === questionId);
        if (!question) {
            return {
                options: [],
                totalVotes: 0,
                totalWeightedVotes: 0
            };
        }

        const options = question.options.map((o, index) => {
            const answerCount = getAnswerCount(questionId, o.optionId);
            return {
                optionId: questionId + '_' + index,
                text: o.text,
                votes: answerCount,
                weightedVotes: answerCount,
                participants: []
            };
        });

        if (question.questionType === QuestionType.Open) {
            const answerCount = getAnswerCount(questionId);
            options.push({
                optionId: questionId + '_open',
                text: 'Open',
                votes: answerCount,
                weightedVotes: answerCount,
                participants: []
            });
        }

        if (question.hasOtherOption) {
            const answerCount = getAnswerCount(questionId);
            options.push({
                optionId: questionId + '_other',
                text: question.otherOptionText || '',
                votes: answerCount,
                weightedVotes: answerCount,
                participants: []
            });
        }

        return {
            options: options,
            totalVotes: getAnsweredCount(questionId),
            totalWeightedVotes: getAnsweredCount(questionId)
        };
    };

    const exportExcelAsync = async () => {
        const xlsx = await import('xlsx');
        const workbook = xlsx.utils.book_new();
        workbook.Props = {
            Application: 'OnlineWebinar.com',
            AppVersion: '1.0.0',
            Author: 'OnlineWebinar.com',
            CreatedDate: new Date(),
            Title: 'Exam results for ' + webinar.title
        };

        const summaryData: (string | number)[][] = [];
        exam.questions.forEach((question, questionIndex) => {
            summaryData.push([questionIndex + 1, question.questionText]);
            question.options.forEach((option) => {
                summaryData.push(['', option.text, getAnswerCount(question.questionId, option.optionId)]);
            });
            if (question.questionType === QuestionType.Open) {
                summaryData.push(['', 'Open', getAnswerCount(question.questionId)]);
            }
            if (question.hasOtherOption) {
                summaryData.push(['', question.otherOptionText || '', getAnswerCount(question.questionId)]);
            }
            summaryData.push([]);
        });

        // prettier-ignore
        const summary = xlsx.utils.aoa_to_sheet([
            ['Exam Results for ' + webinar.title],
            [],
            ['Exported on', , , , new Date()],
            [],
            ['Number of exam questions asked:', , , , exam.questions.length],
            [],
            ...summaryData
        ]);
        xlsx.utils.book_append_sheet(workbook, summary, 'Summary');

        const resultsSheet = xlsx.utils.aoa_to_sheet([
            ['Results for ' + webinar.title],
            [],
            ['First name', 'Last name', 'Nickname', 'Email address', 'Company', 'Country', 'Score', 'Result', 'Time stamp'],
            ...results.map((r) => [
                r.firstName,
                r.lastName,
                r.nickname,
                r.emailAddress,
                r.companyName,
                r.countryCode,
                r.score,
                t(`Insights.Exam.${ExamResultType[r.result]}`),
                r.timestamp
            ])
        ]);
        xlsx.utils.book_append_sheet(workbook, resultsSheet, 'Results');

        exam.questions.forEach((question, questionIndex) => {
            // prettier-ignore
            const data: (number | string | undefined)[][] = [
                [`Details for ${questionIndex + 1}. ${question.questionText}`],
                [],
                ['First name', 'Last name', 'Nickname', 'Email address', 'Company', 'Country', 'Answer', 'Answer value', 'Client type', 'Time stamp'],
            ];

            answers.forEach((examAnswer) => {
                const answer = examAnswer.answers.find((a) => a.questionId === question.questionId);
                if (!answer) {
                    return;
                }

                const participant: (number | string | undefined)[] = [
                    examAnswer.participant.firstName,
                    examAnswer.participant.lastName,
                    examAnswer.participant.nickname,
                    examAnswer.participant.emailAddress,
                    examAnswer.participant.companyName,
                    examAnswer.participant.countryCode
                ];
                answer.selectedAnswers?.forEach((optionId) => {
                    if (optionId === emptyGuid) return; // Skip emptyGuid
                    const option = question.options.find((o) => o.optionId === optionId);
                    data.push([...participant, option?.text, option?.value, ClientType[answer.clientType], answer.timestamp]);
                });
                if (answer.openAnswer) {
                    data.push([
                        ...participant,
                        question.hasOtherOption ? question.otherOptionText || '' : 'Open',
                        answer.openAnswer,
                        ClientType[answer.clientType],
                        answer.timestamp
                    ]);
                }
            });

            const sheet = xlsx.utils.aoa_to_sheet(data);
            xlsx.utils.book_append_sheet(workbook, sheet, 'Question ' + (questionIndex + 1));
        });

        xlsx.writeFileXLSX(workbook, 'ExamResults.xlsx');
    };

    const exportExcel = () => {
        exportExcelAsync().then(() => {
            // Done...
        });
    };

    return (
        <ContainerNoTopSpace>
            <div>
                <PageHeader>
                    <PageHeaderContent>
                        {webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}
                    </PageHeaderContent>
                </PageHeader>

                <div className="px:4 md:px-6 pt-4 md:pt-10 max-w-480">
                    <BackToOriginLink fallback="insights" />
                    <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                        {t('Insights.Exam.PageHeading')}
                    </Heading>

                    <div className="flex flex-col items-start gap-4 mb-8">
                        <div className="bg-white rounded-lg flex items-end py-2 px-4">
                            {t('Insights.Exam.AmountText')}
                            <span className="text-3xl font-medium pl-2">{exam.questions.length}</span>
                        </div>
                        <Button variant="primary" onClick={exportExcel}>
                            {t('Insights.Exam.Button.Export')}
                        </Button>
                    </div>
                    <TabGroup direction={TabGroup.direction.HORIZONTAL}>
                        <TabGroup.TabList numTabs={2} activeTab={activeTab} setActiveTab={changeTab} classNames="border-b border-primary flex justify-center">
                            <TabGroup.Tab
                                activeTab={activeTab}
                                setActiveTab={changeTab}
                                index={0}
                                className="nav-tab-border"
                                activeClassName="nav-tab--active"
                                onClick={() => changeTab(0)}
                            >
                                {t('Insights.Exam.Tab.AnswersOverview')}
                            </TabGroup.Tab>
                            <TabGroup.Tab
                                activeTab={activeTab}
                                setActiveTab={changeTab}
                                index={1}
                                className="nav-tab-border"
                                activeClassName="nav-tab--active"
                                onClick={() => changeTab(1)}
                            >
                                {t('Insights.Exam.Tab.ParticipantDetails')}
                            </TabGroup.Tab>
                        </TabGroup.TabList>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={0}
                            className="grid gap-4 md:gap-6 mt-6 md:mt-8 -mx-2 xs:mx-0 max-w-4xl transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            {exam.questions.map((question, index) => (
                                <PollCard
                                    key={question.questionId}
                                    poll={{
                                        applyWeighting: false,
                                        publicResults: false,
                                        question: question.questionText,
                                        pollId: question.questionId,
                                        ...getExamAnswers(question.questionId)
                                    }}
                                    index={index}
                                ></PollCard>
                            ))}
                        </TabGroup.TabPanel>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={1}
                            className="grid gap-4 md:gap-6 mt-6 md:mt-8 -mx-2 xs:mx-0 transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <ExamResultsDetails results={results} />
                        </TabGroup.TabPanel>
                    </TabGroup>
                </div>
            </div>
        </ContainerNoTopSpace>
    );
};
